import React, { useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, TrendingUp, Code2, Zap, Headphones, Bot, Brain, Users, 
  MessageSquare, RefreshCw, Workflow, Cloud, Database, Target, Layout, Search, 
  GraduationCap, Calendar, Shield, MessageCircle, Eye, Bug, FileCheck, AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { serviceDetails, serviceIdMap } from '../data/serviceDetails';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Icon mapping
const iconMap = {
  TrendingUp, Code2, Zap, Headphones, Bot, Brain, Users, MessageSquare,
  RefreshCw, Workflow, Cloud, Database, Target, Layout, Search, GraduationCap,
  Calendar, Shield, MessageCircle, Eye, Bug, FileCheck, AlertTriangle
};

// Animated counter
const AnimatedCounter = ({ value, suffix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericValue = parseInt(value.replace(/\D/g, ''));
  
  return (
    <span ref={ref}>
      {isInView ? value : '0'}{suffix}
    </span>
  );
};

const ServicePage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  // Find service data
  const services = serviceDetails[language];
  const service = services ? Object.values(services).find(s => s.id === serviceId) : null;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service not found</h1>
          <Link to="/" className="text-[#F7931A] hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const textRevealVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] relative">
      <SEO 
        title={service.title}
        description={service.description}
        image={service.heroImage}
        url={language === 'es' ? `/servicios/${service.id}` : `/services/${service.id}`}
      />
      <div className="grid-pattern fixed inset-0 pointer-events-none" />
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden pt-24">
        {/* Background Image with Parallax */}
        <motion.div 
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0 z-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${service.heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </motion.div>

        {/* Glowing orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#F7931A] rounded-full blur-[200px] opacity-20" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#F7931A] rounded-full blur-[150px] opacity-10" />

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="container-custom relative z-10"
        >
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#F7931A] transition-colors mb-8 group"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            {language === 'es' ? 'Volver al inicio' : 'Back to home'}
          </motion.button>

          <div className="max-w-4xl">
            {/* Service Label */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-6"
            >
              <span className="text-sm font-mono text-[#F7931A] tracking-wider uppercase">
                {language === 'es' ? 'Servicio' : 'Service'}
              </span>
            </motion.div>

            {/* Title with reveal */}
            <div className="overflow-hidden mb-4">
              <motion.h1
                custom={0}
                initial="hidden"
                animate="visible"
                variants={textRevealVariants}
                className="font-['Syne'] text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-white"
              >
                {service.title}
              </motion.h1>
            </div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-2xl md:text-3xl text-[#F7931A] font-['Syne'] font-medium mb-6"
            >
              {service.subtitle}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-lg text-[#A1A1AA] leading-relaxed max-w-2xl mb-8"
            >
              {service.description}
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Link
                to="/#contact"
                className="btn-primary inline-flex items-center gap-2 group"
                data-testid="service-cta"
              >
                {language === 'es' ? 'Solicitar Consulta' : 'Request Consultation'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-[#27272A] rounded-full flex justify-center pt-2">
            <motion.div 
              className="w-1.5 h-3 bg-[#F7931A] rounded-full"
              animate={{ y: [0, 8, 0], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-b border-[#27272A]">
        <div className="container-custom">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {service.stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -5 }}
                className="text-center glass rounded-2xl p-8"
                data-testid={`stat-${index}`}
              >
                <motion.div 
                  className="font-['Syne'] text-5xl md:text-6xl font-bold text-[#F7931A] mb-2"
                  animate={{ 
                    textShadow: [
                      "0 0 20px rgba(247,147,26,0.3)",
                      "0 0 40px rgba(247,147,26,0.5)",
                      "0 0 20px rgba(247,147,26,0.3)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <AnimatedCounter value={stat.value} />
                </motion.div>
                <p className="text-[#A1A1AA]">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F7931A] rounded-full blur-[200px] opacity-5" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-mono text-[#F7931A] tracking-widest uppercase mb-4 block">
              {language === 'es' ? 'Beneficios' : 'Benefits'}
            </span>
            <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {language === 'es' ? '¿Por qué elegirnos?' : 'Why choose us?'}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-6"
          >
            {service.benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon] || CheckCircle;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  whileHover={{ scale: 1.02, y: -10 }}
                  className="glass rounded-2xl p-8 group hover:border-[#F7931A]/50 transition-all duration-500"
                  data-testid={`benefit-${index}`}
                >
                  <motion.div 
                    className="w-16 h-16 rounded-2xl bg-[#F7931A]/10 flex items-center justify-center mb-6 group-hover:bg-[#F7931A]/20 transition-colors"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <IconComponent className="w-8 h-8 text-[#F7931A]" />
                  </motion.div>
                  <h3 className="font-['Syne'] text-2xl font-bold text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    {benefit.description}
                  </p>
                  <motion.div 
                    className="w-0 h-1 bg-[#F7931A] mt-6 group-hover:w-full transition-all duration-500"
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F7931A] rounded-full blur-[200px] opacity-5" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-mono text-[#F7931A] tracking-widest uppercase mb-4 block">
              {language === 'es' ? 'Proceso' : 'Process'}
            </span>
            <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {language === 'es' ? 'Cómo trabajamos' : 'How we work'}
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#F7931A] via-[#F7931A]/50 to-transparent" />

            <div className="space-y-12">
              {service.process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-row`}
                  data-testid={`process-step-${index}`}
                >
                  {/* Step number */}
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-8 h-8 rounded-full bg-[#F7931A] flex items-center justify-center font-bold text-[#050505] z-10"
                  >
                    {step.step}
                  </motion.div>

                  {/* Content card */}
                  <motion.div
                    whileHover={{ scale: 1.02, y: -5 }}
                    className={`ml-16 md:ml-0 ${
                      index % 2 === 0 ? 'md:mr-auto md:pr-16' : 'md:ml-auto md:pl-16'
                    } md:w-[45%] glass rounded-2xl p-6 group hover:border-[#F7931A]/30 transition-all duration-300`}
                  >
                    <h3 className="font-['Syne'] text-xl font-bold text-white mb-2 flex items-center gap-2">
                      <span className="text-[#F7931A] md:hidden">{step.step}.</span>
                      {step.title}
                    </h3>
                    <p className="text-[#A1A1AA]">{step.description}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-24 md:py-32 relative">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-mono text-[#F7931A] tracking-widest uppercase mb-4 block">
              {language === 'es' ? 'Stack Tecnológico' : 'Tech Stack'}
            </span>
            <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {language === 'es' ? 'Tecnologías que usamos' : 'Technologies we use'}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-4"
          >
            {service.technologies.map((tech, index) => (
              <motion.span
                key={index}
                variants={staggerItem}
                whileHover={{ 
                  scale: 1.1, 
                  y: -5,
                  backgroundColor: "rgba(247, 147, 26, 0.2)",
                  borderColor: "rgba(247, 147, 26, 0.5)"
                }}
                className="px-6 py-3 glass rounded-full text-white hover:text-[#F7931A] transition-all duration-300 cursor-default font-medium"
                data-testid={`tech-${index}`}
              >
                {tech}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#F7931A]/10 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F7931A] rounded-full blur-[300px] opacity-10" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {language === 'es' 
                ? '¿Listo para transformar tu negocio?' 
                : 'Ready to transform your business?'}
            </h2>
            <p className="text-xl text-[#A1A1AA] mb-10">
              {language === 'es'
                ? 'Agenda una consulta gratuita y descubre cómo podemos ayudarte.'
                : 'Schedule a free consultation and discover how we can help you.'}
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/#contact"
                className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-5 group"
                data-testid="final-cta"
              >
                {language === 'es' ? 'Contactar Ahora' : 'Contact Now'}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ServicePage;
